import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Sprout, Loader2, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatError = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
};

const AdminLogin = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { username, password });
      localStorage.setItem("lfo_admin_token", data.token);
      navigate("/admin");
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center px-6 pt-16" data-testid="admin-login-page">
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="max-w-md w-full rounded-3xl border border-[#E2E8E4] bg-[#F2F4F0] p-10"
        data-testid="admin-login-form"
      >
        <span className="w-12 h-12 rounded-full bg-[#C4D7D1] flex items-center justify-center">
          <Lock size={20} className="text-[#1E352A]" />
        </span>
        <h1 className="font-display text-3xl mt-4 text-[#1A2F24]">Admin Login</h1>
        <p className="mt-2 text-sm text-[#5C7368]">Living Fruit Outreach Inc — website administration</p>
        <div className="mt-6 space-y-4">
          <input required placeholder="Username" value={username} onChange={(e) => setUsername(e.target.value)}
            data-testid="admin-username-input" autoComplete="username"
            className="w-full rounded-xl bg-white/70 border border-[#E2E8E4] px-4 py-3 text-sm text-[#1A2F24] placeholder:text-[#5C7368]/60 focus:outline-none focus:border-[#F4A261] transition-colors duration-300" />
          <input required type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-password-input" autoComplete="current-password"
            className="w-full rounded-xl bg-white/70 border border-[#E2E8E4] px-4 py-3 text-sm text-[#1A2F24] placeholder:text-[#5C7368]/60 focus:outline-none focus:border-[#F4A261] transition-colors duration-300" />
        </div>
        {error && <p className="mt-4 text-sm text-red-700" data-testid="admin-login-error">{error}</p>}
        <button disabled={loading} data-testid="admin-login-submit"
          className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#1E352A] text-[#FAF9F6] py-3.5 text-sm tracking-[0.12em] uppercase transition-colors duration-300 hover:bg-[#F4A261] hover:text-white disabled:opacity-60">
          {loading ? <Loader2 size={15} className="animate-spin" /> : <Sprout size={15} />} Log In
        </button>
        <Link to="/" data-testid="admin-login-back" className="mt-5 block text-center text-xs text-[#5C7368] hover:text-[#F4A261] transition-colors duration-300">
          ← Back to website
        </Link>
      </motion.form>
    </main>
  );
};

export default AdminLogin;
